import React, { useState, useEffect } from 'react';
import { post } from 'axios';
import { MDBRow, MDBCol } from 'mdbreact';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { actionSvgs, contentSvgs } from 'assets/icons';

import { renderToast } from 'components/Informations/Toast/utils';

import Accordion from 'components/Accordion';
import FilledButton from 'components/Buttons/FilledButton';
import PageStructure from 'components/PageStructure';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';

import colors from 'themes/Main';

import endpoints from 'services/endpoints';
import { arabicToRoman } from 'utils/numberManipulations';
import { formatDate } from 'utils/moment';

import useFetch from 'hooks/useFetch';
import { renderOverlay } from './utils';
import * as S from './styles';

const { darkBlue } = colors;

const {
  getPDIHistory,
  changePDISolicitation,
  getAllPDISolicitations,
  getJustifications,
} = endpoints;

export default function PDI({ history, user }) {
  const { id_funcionario: idFuncionario } = user;

  const [hasNewPdi, setHasNewPdi] = useState(false);

  const [solicitationsTableFilter, setSolicitationsTableFilter] = useState(
    'nome_funcionario',
  );
  const [mediationsTableFilter, setMediationsTableFilter] = useState(
    'nome_funcionario',
  );
  const [evaluationsTableFilter, setEvaluationsTableFilter] = useState(
    'nome_funcionario',
  );

  const [pdiHistory, setPdiHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [mediations, setMediations] = useState([]);
  const [evaluations, setEvaluations] = useState([]);

  const [openHistoryAccordion, setOpenHistoryAccordion] = useState(false);

  const [loading, setLoading] = useState(false);
  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: '',
  });
  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');
  const [overlayData, setOverlayData] = useState({
    name: '',
    justifications: [],
  });

  const [evaluationJustifications, setEvaluationJustifications] = useState([]);
  const [mediationJustifications, setMediationJustifications] = useState([]);

  const [observeSolicitations, setObserveSolicitations] = useState(false);

  const [allSolicitations] = useFetch(
    `${getAllPDISolicitations}/${idFuncionario}`,
    observeSolicitations,
  );
  const [allJustifications] = useFetch(getJustifications);
  const [allHistory] = useFetch(`${getPDIHistory}/${idFuncionario}`);

  useEffect(() => {
    (async () => {
      const solicitationsData = await allSolicitations;
      const justifications = await fetch(getJustifications)
        .then(response => response.json())
        .then(responseJson => responseJson);

      const { mediacoes, avaliacoes } = await solicitationsData;

      const orderedMediations = _.orderBy(
        _.uniqBy(mediacoes, 'id_solicitacao'),
        'funcionario_mediado',
      );

      const mediationJust = justifications
        ?.filter(
          ({ escopo, tipo_justificativa: tipo }) =>
            escopo === 'Mediação' && tipo === 'Rejeição',
        )
        .map(mediation => ({
          label: mediation.descricao,
          name: mediation.id_justificativa,
          value: mediation.id_justificativa,
          ...mediation,
        }));

      const orderedEvaluations = _.orderBy(
        _.uniqBy(avaliacoes, 'id_solicitacao'),
        'funcionario_mediado',
      );
      const evaluationJust = justifications
        ?.filter(
          ({ escopo, tipo_justificativa: tipo }) =>
            escopo === 'Avaliação' && tipo === 'Rejeição',
        )
        .map(evaluation => ({
          label: evaluation.descricao,
          name: evaluation.id_justificativa,
          value: evaluation.id_justificativa,
          ...evaluation,
        }));

      const allMediations = orderedMediations.map(mediation => ({
        ...mediation,
        nome_funcionario: mediation.funcionario_mediado,
        type: 'mediation',
      }));

      const allEvaluations = orderedEvaluations.map(evaluation => ({
        ...evaluation,
        nome_funcionario: evaluation.funcionario_avaliado,
        type: 'evaluation',
      }));

      const allRequests = _.orderBy(
        [...allEvaluations, ...allMediations],
        ['nome_funcionario'],
      );

      const filteredMediations = allMediations.filter(({ aceite }) => !!aceite);
      const filteredEvaluations = allEvaluations.filter(
        ({ aceite }) => !!aceite,
      );

      setMediationJustifications(mediationJust);
      setEvaluationJustifications(evaluationJust);

      setRequests(allRequests);
      setMediations(filteredMediations);
      setEvaluations(filteredEvaluations);

      return solicitationsData &&
        justifications &&
        allEvaluations &&
        allRequests &&
        allMediations
        ? setLoading(false)
        : setLoading(true);
    })();
  }, [allSolicitations, allJustifications, observeSolicitations]);

  useEffect(() => {
    (async () => {
      const { autoavaliacao, avaliacoes } = await allHistory;

      setPdiHistory(avaliacoes || []);
      return autoavaliacao ? setHasNewPdi(true) : setHasNewPdi(false);
    })();
  }, [allHistory]);

  const initSelfEvaluation = () => {
    history.push({
      pathname: '/auth/pdi/tutorial',
      state: {
        context: 'self-evaluation',
        data: {
          idFuncionario,
        },
      },
    });
  };

  const handleTableFilter = (tableName, filterBy) => {
    switch (tableName) {
      case 'solicitations':
        return [
          setRequests(_.orderBy(requests, [filterBy])),
          setSolicitationsTableFilter(filterBy),
        ];
      case 'mediations':
        return [
          setMediations(_.orderBy(mediations, [filterBy])),
          setMediationsTableFilter(filterBy),
        ];
      case 'evaluations':
        return [
          setEvaluations(_.orderBy(evaluations, [filterBy])),
          setEvaluationsTableFilter(filterBy),
        ];
      default:
        return null;
    }
  };

  const handleAccept = async id => {
    const objectToSend = {
      aceite: 1,
      id_usr_up: idFuncionario,
      id_solicitacao: id,
    };

    try {
      await post(changePDISolicitation, objectToSend)
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'information',
            message: 'Solicitação aceita',
            closeTime: 6000,
          });
        })
        .then(() => setObserveSolicitations(true));
    } catch {
      setObserveSolicitations(true);
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message: 'Erro ao aceitar solicitação. Tente novamente',
        closeTime: 6000,
      });
    }
  };

  const handleRefuseSolicitation = async content => {
    const { id_solicitacao: idSolicitacao, selectedOption } = content;

    const objectToSend = {
      aceite: 0,
      id_usr_up: idFuncionario,
      id_justificativa_funcionario: selectedOption,
      id_solicitacao: idSolicitacao,
    };

    await post(changePDISolicitation, objectToSend);
  };

  const renderStatusTable = (type, data) =>
    data.map(
      ({
        id_solicitacao: idSolicitacao,
        descricao,
        aceite,
        nome_funcionario: nomeFuncionario,
        id_funcionario_mediador: idMediador,
        id_configuracao_avaliacao: idConfig,
        id_avaliacao: idAvaliacao,
      }) => {
        const currentStatus = descricao !== 0 && descricao.toLowerCase();
        const currentType = type === 'mediation' ? 'Mediação' : 'Avaliação';

        const redirect = () => {
          if (type === 'mediation') {
            return {
              pathname: '/auth/pdi/tutorial',
              state: {
                context: 'mediation',
                data: { idAvaliacao, idMediador, idFuncionario },
              },
            };
          }

          return {
            pathname: '/auth/pdi/tutorial',
            state: {
              context: 'evaluation',
              data: {
                idAvaliacao,
                idAvaliador: idFuncionario,
                idFuncionario,
                idConfig,
              },
            },
          };
        };

        return (
          !!aceite && (
            <tr key={idSolicitacao}>
              <td>{nomeFuncionario}</td>

              <td className={currentStatus.split(' ')[1]}>
                {currentStatus.includes('pendente')
                  ? `${currentType} pendente`
                  : `${currentType} em andamento`}
              </td>

              {currentStatus.includes('aceita') ? (
                <td>
                  <S.ActionLink to={redirect} className="no-icon">
                    {type === 'mediation' ? 'Mediar' : 'Avaliar'}
                  </S.ActionLink>
                </td>
              ) : (
                <td>
                  <S.ActionLink to={redirect} className="no-icon">
                    {type === 'mediation'
                      ? 'Continuar mediação'
                      : 'Continuar avaliação'}
                  </S.ActionLink>
                </td>
              )}
            </tr>
          )
        );
      },
    );

  return (
    <>
      {overlayVisibility &&
        renderOverlay(
          setOverlayType,
          overlayType,
          setOverlayVisibility,
          overlayVisibility,
          overlayData,
        )}

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      <PageStructure
        title="Plano de Desenvolvimento Individual"
        subtitle="Veja os resultados da sua avaliação e as solicitações de mediação que você recebeu"
      >
        <MDBRow>
          <MDBCol lg="9" sm="12">
            <Accordion
              accordionOpen={openHistoryAccordion}
              setAccordionOpen={setOpenHistoryAccordion}
              callbackText="Não há resultados disponíveis no momento"
              headText="Histórico de resultados"
              hasBgColor
              bgColor={darkBlue}
            >
              <S.HistoryTable>
                <div className="table-responsive">
                  {pdiHistory && pdiHistory.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Data</th>
                          <th scope="col">Senioridade</th>
                          <th scope="col">Resultado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdiHistory.map(
                          ({
                            id_avaliacao: idAvaliacao,
                            dt_inicio_preenchimento: startDate,
                            senioridade,
                            resultado_individual: resultado,
                          }) => {
                            const seniorLevel = senioridade.split(' ');

                            return (
                              <tr key={idAvaliacao}>
                                <td>
                                  {formatDate(
                                    new Date(startDate),
                                    'DD/MM/YYYY',
                                  )}
                                </td>
                                <td>
                                  {`${seniorLevel[0]} ${arabicToRoman(
                                    seniorLevel[1],
                                  )}`}
                                </td>
                                <td>{resultado}</td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <S.TableCallback>
                      <p>Não há resultados disponíveis no momento</p>
                    </S.TableCallback>
                  )}
                </div>
              </S.HistoryTable>
            </Accordion>
          </MDBCol>
          <MDBCol lg="3" sm="12">
            <S.TestButton>
              <FilledButton
                text="Autoavaliação"
                disabled={!hasNewPdi}
                hasTooltip
                tooltipText="A avaliação não está disponível no momento."
                handleClick={initSelfEvaluation}
              />
            </S.TestButton>
          </MDBCol>
        </MDBRow>
        {!!requests && requests.length > 0 && (
          <MDBRow>
            <MDBCol lg="12">
              <S.TableTitle>Solicitações recebidas</S.TableTitle>
              <S.RecievedSolicitations>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          onClick={() =>
                            handleTableFilter(
                              'solicitations',
                              'nome_funcionario',
                            )
                          }
                        >
                          Funcionário
                          {solicitationsTableFilter === 'nome_funcionario' &&
                            contentSvgs('tableArrow')}
                        </th>

                        <th
                          scope="col"
                          onClick={() => {
                            handleTableFilter('solicitations', 'type');
                          }}
                        >
                          Tipo de solicitação
                          {evaluationsTableFilter === 'type' &&
                            contentSvgs('tableArrow')}
                        </th>

                        <th scope="col">Justificativa</th>
                        <th scope="col">Status</th>
                        <th scope="col">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map(
                        ({
                          id_solicitacao: idSolicitacao,
                          id_funcionario_mediador: idFuncionarioMediador,
                          id_autoavaliacao: idAutoavaliacao,
                          justificativa_gestor: justificativaGestor,
                          aceite,
                          nome_funcionario: nomeFuncionario,
                          type,
                        }) =>
                          !aceite && (
                            <tr key={idSolicitacao}>
                              <td>{nomeFuncionario}</td>

                              <td className={`type-${type}`}>
                                {type === 'mediation'
                                  ? 'Mediação'
                                  : 'Avaliação'}
                              </td>

                              {type === 'evaluation' ? (
                                <td>{justificativaGestor}</td>
                              ) : (
                                <td className="no-applicable">Não aplicável</td>
                              )}

                              {type === 'evaluation' ? (
                                <td className="no-applicable">Não aplicável</td>
                              ) : (
                                <td>
                                  <S.ActionButton
                                    onClick={() => {
                                      setOverlayVisibility(true);
                                      setOverlayType('previewMediation');
                                      setOverlayData({
                                        idMediator: idFuncionarioMediador,
                                        idEvaluation: idAutoavaliacao,
                                      });
                                    }}
                                    className="no-icon"
                                  >
                                    Pré visualizar
                                  </S.ActionButton>
                                </td>
                              )}

                              <td>
                                <S.ActionButton
                                  onClick={() => handleAccept(idSolicitacao)}
                                >
                                  {actionSvgs('accept')}
                                </S.ActionButton>
                                <S.ActionButton
                                  onClick={() => {
                                    setOverlayVisibility(true);

                                    return type === 'evaluation'
                                      ? [
                                          setOverlayData({
                                            employeeName: nomeFuncionario,
                                            justifications: evaluationJustifications,
                                            setObserver: setObserveSolicitations,
                                            requestAction: handleRefuseSolicitation,
                                            additionalContent: {
                                              id_solicitacao: idSolicitacao,
                                            },
                                          }),
                                          setOverlayType('refuseEvaluation'),
                                        ]
                                      : [
                                          setOverlayData({
                                            employeeName: nomeFuncionario,
                                            justifications: mediationJustifications,
                                            setObserver: setObserveSolicitations,
                                            requestAction: handleRefuseSolicitation,
                                            additionalContent: {
                                              id_solicitacao: idSolicitacao,
                                            },
                                          }),
                                          setOverlayType('refuseMediation'),
                                        ];
                                  }}
                                >
                                  {actionSvgs('refuse')}
                                </S.ActionButton>
                              </td>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </table>
                </div>
              </S.RecievedSolicitations>
            </MDBCol>
          </MDBRow>
        )}
        {!!mediations && mediations.length > 0 && (
          <MDBRow>
            <MDBCol lg="12">
              <S.TableTitle>Status de mediação</S.TableTitle>
              <S.MediationTable>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          onClick={() => {
                            handleTableFilter('mediations', 'nome_funcionario');
                          }}
                        >
                          Funcionário mediado
                          {mediationsTableFilter === 'nome_funcionario' &&
                            contentSvgs('tableArrow')}
                        </th>
                        <th
                          scope="col"
                          onClick={() => {
                            handleTableFilter('mediations', 'status');
                          }}
                        >
                          Status
                          {mediationsTableFilter === 'status' &&
                            contentSvgs('tableArrow')}
                        </th>
                        <th scope="col">Ação</th>
                      </tr>
                    </thead>
                    <tbody>{renderStatusTable('mediation', mediations)}</tbody>
                  </table>
                </div>
              </S.MediationTable>
            </MDBCol>
          </MDBRow>
        )}
        {!!evaluations && evaluations.length > 0 && (
          <MDBRow>
            <MDBCol lg="12">
              <S.TableTitle>Status de avaliação</S.TableTitle>
              <S.EvaluationTable>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          onClick={() => {
                            handleTableFilter(
                              'evaluations',
                              'nome_funcionario',
                            );
                          }}
                        >
                          Funcionário avaliado
                          {evaluationsTableFilter === 'nome_funcionario' &&
                            contentSvgs('tableArrow')}
                        </th>
                        <th
                          scope="col"
                          onClick={() => {
                            handleTableFilter('evaluations', 'status');
                          }}
                        >
                          Status
                          {evaluationsTableFilter === 'status' &&
                            contentSvgs('tableArrow')}
                        </th>
                        <th scope="col">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderStatusTable('evaluation', evaluations)}
                    </tbody>
                  </table>
                </div>
              </S.EvaluationTable>
            </MDBCol>
          </MDBRow>
        )}
      </PageStructure>

      {loading && <ContentLoader isFull />}
    </>
  );
}

PDI.defaultProps = {
  history: {},
  user: {},
};

PDI.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
